import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, initFirebase } from '../firebase';
import { 
  onAuthStateChanged, 
  signInWithPopup, 
  GoogleAuthProvider, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  deleteUser
} from 'firebase/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    let unsubscribe;
    
    const initialize = async () => {
      try {
        const { auth: firebaseAuth } = await initFirebase();
        
        unsubscribe = onAuthStateChanged(firebaseAuth, async (currentUser) => {
          if (currentUser) {
            setUser({
              uid: currentUser.uid,
              email: currentUser.email,
              name: currentUser.displayName || currentUser.email?.split('@')[0],
              _firebaseUser: currentUser
            });
            const jwt = await currentUser.getIdToken();
            setToken(jwt);
          } else {
            setUser(null);
            setToken(null);
          }
          setLoading(false);
        });
      } catch (error) {
        console.error("Auth context failed to initialize Firebase:", error);
        setLoading(false);
      }
    };

    initialize();
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const refreshToken = async () => {
    try {
      if (!auth.currentUser) return null;
      const jwt = await auth.currentUser.getIdToken(true);
      setToken(jwt);
      return jwt;
    } catch {
      return null;
    }
  };

  const googleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  const emailSignUp = async (email, password) => {
    if (!email) throw new Error('Email is required');
    if (password.length < 6) throw new Error('Password must be at least 6 characters');
    await createUserWithEmailAndPassword(auth, email, password);
  };

  const emailSignIn = async (email, password) => {
    if (!email) throw new Error('Email is required');
    await signInWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    await signOut(auth);
  };

  const updateUserName = async (displayName) => {
    if (!auth.currentUser) throw new Error('Not authenticated');
    await updateProfile(auth.currentUser, { displayName });
    // Update local state to reflect immediately
    setUser(prev => ({ ...prev, name: displayName, _firebaseUser: { ...prev._firebaseUser, displayName } }));
  };

  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  const deleteAccount = async () => {
    if (!auth.currentUser) throw new Error('Not authenticated');
    await deleteUser(auth.currentUser);
    setUser(null);
    setToken(null);
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      refreshToken, googleSignIn, emailSignUp, emailSignIn, logout,
      updateUserName, resetPassword, deleteAccount
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
